/**
 * Route permission configuration extracted from menuConfig
 * This serves as the single source of truth for route-based permissions
 *
 * Public routes: Always accessible (no permission check needed)
 * Permissioned routes: Require specific permission key for STAFF users
 */

export interface RoutePermission {
  permission?: string;
  isPublic?: boolean;
}

/**
 * Route permissions map
 * Key: Route path
 * Value: Permission configuration
 */
export const routePermissions: Record<string, RoutePermission> = {
  // Public routes - always accessible
  "/welcome": { isPublic: true },
  "/logout": { isPublic: true },

  // Site section
  "/template": { permission: "regularSchedule" },
  "/template/add": { permission: "regularSchedule" },
  "/template/update/:id": { permission: "regularSchedule" },
  "/template/add/:id/set/schedule": { permission: "regularSchedule" },
  "/template/update/:id/set/schedule": { permission: "regularSchedule" },

  "/pre-schedule": { permission: "preSchedule" },
  "/pre-schedule/add": { permission: "preSchedule" },
  "/pre-schedule/update/:id": { permission: "preSchedule" },
  "/pre-schedule/add/:id/set/schedule": { permission: "preSchedule" },
  "/pre-schedule/update/:id/set/schedule": { permission: "preSchedule" },

  "/schedule/daily": { permission: "todaySchedule" },
  "/schedule/daily/:siteId/s_template": { permission: "todaySchedule" },

  "/s-car": { permission: "carProfile" },
  "/s-car/add": { permission: "carProfile" },
  "/s-car/update/:id": { permission: "carProfile" },

  "/s-location": {},
  "/s-location/add": {},
  "/s-location/update/:id": {},

  "/s-news": {},
  "/s-news/add": {},
  "/s-news/update/:id": {},

  "/s-userManual": {},

  "/s-app-info": {},

  "/privacyPolicy": {},

  "/termsCondition": {},

  "/notesToPassenger": {},

  "/notes-to-passenger-web": {},

  "/download-app": {},

  // Notification section
  "/news": { permission: "pushNotification" },
  "/news/add": { permission: "pushNotification" },
  "/news/update/:id": { permission: "pushNotification" },

  "/drivernews": { permission: "pushNotification" },
  "/drivernews/add": { permission: "pushNotification" },
  "/drivernews/update/:id": { permission: "pushNotification" },

  // Report section
  "/lateReport": { permission: "report" },
  "/lateReport/add": { permission: "report" },
  "/lateReport/edit/:id": { permission: "report" },

  "/ScheduleReport": { permission: "report" },
  "/ScheduleReport/Add": { permission: "report" },
  "/ScheduleReport/update/:id": { permission: "report" },
  "/ScheduleReport/details/:schedule_id/:location_id": { permission: "report" },

  // General Setting - Site
  "/site": { permission: "site" },
  "/site/add": { permission: "site" },
  "/site/update/:id": { permission: "site" },

  // General Setting - User section
  "/user": { permission: "driverProfile" },
  "/user/add": { permission: "driverProfile" },
  "/user/update/:id": { permission: "driverProfile" },

  "/admin": {},
  "/admin/add": {},
  "/admin/update/:id": {},

  "/mo": {},
  "/mo/add": {},
  "/mo/update/:id": {},

  "/staff": {},
  "/staff/add": {},
  "/staff/update/:id": {},

  "/staff/permission": {},

  // General Setting - Other routes
  "/car": { permission: "carProfile" },
  "/car/add": { permission: "carProfile" },
  "/car/update/:id": { permission: "carProfile" },

  "/location": {},
  "/location/add": {},
  "/location/update/:id": {},

  "/path": {},
  "/path/add": {},
  "/path/update/:id": {},

  "/PathGroup": {},
  "/PathGroup/add": {},
  "/PathGroup/update/:id": {},

  "/cancel-schedule": {},

  "/companyInfo": {},

  "/contact-us": {},

  "/driverFcmConfig": {},

  "/otherInfo": {},

  "/reachedValue": {},

  "/logoImage": {},

  "/reason": {},
  "/reason/add": {},
  "/reason/update/:id": {},

  "/notification": {},

  "/appinfo": {},

  // Company Service section
  "/companyService/list": {},
  "/companyService/add": {},
  "/companyService/details/:id": {},

  "/companyService/inquiryService": {},
  "/companyService/inquiryService/:id": {},

  "/companyService/serviceTag": {},
  "/companyService/serviceTag/add": {},
  "/companyService/serviceTag/:id": {},

  // Excel Report section
  "/export/daily-report": { permission: "excelReport" },

  "/export/monthly-report": { permission: "excelReport" },

  "/export/monthly-manulife-report": { permission: "excelReport" },
};

/**
 * Get permission config for a specific route path
 * @param path - Route path
 * @returns Permission configuration or undefined
 */
export const getRoutePermission = (
  path: string
): RoutePermission | undefined => {
  // Try exact match first
  if (routePermissions[path]) {
    return routePermissions[path];
  }

  // Try to match dynamic routes by replacing :id with placeholder
  const normalizedPath = path.replace(/\/\d+/g, "/:id");
  if (routePermissions[normalizedPath]) {
    return routePermissions[normalizedPath];
  }

  // No permission found - route requires explicit permission for STAFF
  // Return undefined so hasRouteAccess will deny access for STAFF users
  return undefined;
};

/**
 * Check if a path requires specific permission
 * @param path - Route path
 * @returns Permission key or null if public
 */
export const getPathPermissionKey = (path: string): string | null => {
  const config = getRoutePermission(path);
  return config?.permission || null;
};
